import { toUiDeliveryChannel } from "./communication/DeliveryChannelSection";
import type { ReminderAction } from "../app/utils/reminderAction";
import { CHANNEL_EMAIL_LC } from "../app/constants/communicationChannel";

/**
 * Was der Mahn-Assistent zum Öffnen braucht — in **einem** Schritt beschafft.
 *
 * Der Vorgang ist zweistufig: erst der Fall (`assistant-context`), dann — abhängig davon — der
 * Kanalvorschlag samt Vorlagen und Brief-Integrationen. Beides stand vorher in einem Effekt mit
 * eigenem `AbortController` und sechs `setState`-Aufrufen dazwischen; jeder Zwischenstand war für
 * einen Lidschlag sichtbar.
 *
 * Hier steht nur die Beschaffung, ohne React. Dadurch ist sie einzeln prüfbar
 * (`reminderAssistantData.test.ts`) — vor allem der Fall „Inkasso-Übergabe", in dem der Kanal
 * feststeht und gar nicht erst vorgeschlagen wird, und der Fall „Vorschlag scheitert", nach dem der
 * Assistent trotzdem einen brauchbaren Zustand zeigen muss.
 */

export type ReminderAssistantOpenItem = {
  openItemId: string;
  openAmount: number;
  description: string;
  dueDate: string;
  reason: "regular" | "chargeback" | "chargebackFee";
  /** Vertrag der Position — bei mehreren Verträgen in einem Schreiben die einzige Unterscheidung. */
  contractName?: string | null;
  /** Stufe dieser Position; sie kann unter der Stufe des Schreibens liegen. */
  targetReminderLevel: number;
  targetReminderLevelName: string;
};

export type ReminderAssistantContext = {
  action?: ReminderAction;
  suggestedCollectionDate?: string | null;
  collectionUnavailableReasonKey?: string | null;
  personId: string;
  personName: string;
  personEmail?: string | null;
  openItemId: string;
  reminderLevelId: string;
  reminderLevelName: string;
  reminderLevelLevel: number;
  studioId?: string | null;
  studioName?: string | null;
  openItems: ReminderAssistantOpenItem[];
  totalOpenAmount: number;
  reminderFeeAmount: number;
  totalAmount: number;
  reminderReason: "regular" | "chargeback" | "mixed";
  regularOpenAmount: number;
  chargebackOpenAmount: number;
  chargebackFeeOpenAmount: number;
  debtCollectionAgencyId?: string | null;
};

export type SuggestReminderChannelResponse = {
  channel?: string | null;
  allowedChannels: string[];
  unavailableReasonKey?: string | null;
  /** Textvariante der Mahnstufe; leer = ausgelieferter Grundfall. */
  variantId?: string | null;
};

export type LetterSendingIntegrationDto = { id: string; integrationType: string; displayName: string };

export type ReminderAssistantData = {
  context: ReminderAssistantContext;
  debtCollectionAgencyId: string | null;
  allowedChannels: string[];
  channelUnavailableReasonKey: string | null;
  letterSendingIntegrations: LetterSendingIntegrationDto[];
  suggestedChannel?: string;
  /** Textvariante der Mahnstufe; leer = ausgelieferter Grundfall. */
  suggestedVariantId?: string;
};

export type ReminderAssistantApi = {
  get<T>(url: string): Promise<T>;
  post<T>(url: string, body?: unknown): Promise<T>;
};

export type ReminderAssistantRequest = {
  personId: string;
  openItemId: string;
  ignoreReminderDelay: boolean;
  /**
   * Auswahl des Bedieners; leer = ganzer Mahnvorgang. Die Auswahl ändert Umfang, Summe und — wenn
   * dadurch die führende Mahnkette wegfällt — auch die Stufe des Schreibens. Deshalb löst der
   * Server sie neu auf, statt dass die Anzeige nachrechnet.
   */
  selectedOpenItemIds?: string[];
};

/** Schlüsselbestandteile des Abrufs — hält Query-Key und Anfrage beieinander. */
export function reminderAssistantQueryParts(request: ReminderAssistantRequest) {
  return {
    personId: request.personId,
    openItemId: request.openItemId,
    ignoreReminderDelay: request.ignoreReminderDelay,
    selectedOpenItemIds: [...(request.selectedOpenItemIds ?? [])].sort().join(",")
  };
}

export async function loadReminderAssistantData(
  api: ReminderAssistantApi,
  request: ReminderAssistantRequest
): Promise<ReminderAssistantData> {
  const params = new URLSearchParams({
    personId: request.personId,
    openItemId: request.openItemId,
    ignoreReminderDelay: String(request.ignoreReminderDelay)
  });
  for (const openItemId of request.selectedOpenItemIds ?? []) params.append("openItemIds", openItemId);
  const context = await api.get<ReminderAssistantContext>(`/api/v1/reminders/assistant-context?${params.toString()}`);
  if (!context) throw new Error("Reminder assistant context missing");

  const debtCollectionAgencyId = context.debtCollectionAgencyId ?? null;

  // Inkasso-Übergabestufe: `suggest-channel` ist hier nicht zuständig — der Kanal **ist** die
  // Übergabe. Ein Vorschlag könnte sie nur überstimmen.
  if (debtCollectionAgencyId) {
    return {
      context,
      debtCollectionAgencyId,
      allowedChannels: ["debt_collection"],
      channelUnavailableReasonKey: null,
      letterSendingIntegrations: [],
      suggestedChannel: "debt_collection"
    };
  }

  try {
    const [suggestion, integrations] = await Promise.all([
      api.post<SuggestReminderChannelResponse>("/api/v1/reminders/suggest-channel", {
        personId: context.personId,
        openItemId: context.openItemId,
        reminderLevelId: context.reminderLevelId,
        studioId: context.studioId || null,
        ignoreReminderDelay: request.ignoreReminderDelay,
        // Die Auswahl bestimmt den Mahngrund (regulär, Rücklastschrift, gemischt) und damit die Route.
        openItemIds: request.selectedOpenItemIds ?? null
      }),
      api.get<LetterSendingIntegrationDto[]>("/api/v1/integrations/by-capability/LetterSending")
    ]);

    return {
      context,
      debtCollectionAgencyId: null,
      allowedChannels: (suggestion?.allowedChannels ?? []).map((channel) => toUiDeliveryChannel(channel)),
      channelUnavailableReasonKey: suggestion?.unavailableReasonKey ?? null,
      letterSendingIntegrations: integrations ?? [],
      suggestedChannel: suggestion?.channel ? toUiDeliveryChannel(String(suggestion.channel)) : undefined,
      suggestedVariantId: suggestion?.variantId ?? undefined
    };
  } catch {
    // Der Fall selbst steht — nur der Vorschlag fehlt. Den Assistenten deswegen ganz zu verweigern
    // wäre falsch: der Sachbearbeiter sieht den Fall und bekommt die Begründung, warum kein Kanal
    // angeboten wird.
    return {
      context,
      debtCollectionAgencyId: null,
      allowedChannels: [],
      channelUnavailableReasonKey: "ReminderChannelUnavailable",
      letterSendingIntegrations: []
    };
  }
}

/** Der Kanal, mit dem der Assistent aufgeht: der Vorschlag, sonst der einzige mögliche. */
export function initialReminderChannel(data: ReminderAssistantData): string | undefined {
  // Konfigurierte Kanäle bleiben sichtbar; ohne Empfängeradresse ist E-Mail aber
  // deaktiviert und darf auch als einziger konfigurierter Kanal nicht vorbelegt werden.
  const selectable = data.allowedChannels.filter(channel => channel !== CHANNEL_EMAIL_LC || Boolean(data.context.personEmail?.trim()));
  if (data.suggestedChannel && selectable.includes(data.suggestedChannel)) return data.suggestedChannel;
  return selectable.length === 1 ? selectable[0] : undefined;
}
